import { Popover } from 'antd';
import Minus from 'assets/svg/Minus';
import Plus from 'assets/svg/Plus';
import DownSvg from 'assets/svg/Down';
import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { without } from 'lodash';
import { FilterContextData } from 'contexts/FilterContext';
import styled from '@emotion/styled';
import tw from 'twin.macro';

const TierMultiSelectionPanel = ({
    localMultiSelectedTiers,
    profitPoolTierList,
    setLocalMultiSelectedTiers,
    dropdownRef,
}: {
    localMultiSelectedTiers: string[];
    profitPoolTierList: any[];
    setLocalMultiSelectedTiers: Function;
    dropdownRef: React.RefObject<HTMLDivElement>;
}) => {
    const allTierReferences = profitPoolTierList?.map((tier: any) => tier.tierReference) || [];
    const allTiersSelected =
        allTierReferences.length > 0 && localMultiSelectedTiers.length === allTierReferences.length;

    const handleSelectAll = () => {
        if (allTiersSelected) {
            // If all are selected, deselect all
            setLocalMultiSelectedTiers([]);
        } else {
            // Select all tiers
            setLocalMultiSelectedTiers([...allTierReferences]);
        }
    };

    const handleTierToggle = (tierReference: string) => {
        if (localMultiSelectedTiers.includes(tierReference)) {
            // Remove tier
            setLocalMultiSelectedTiers(without(localMultiSelectedTiers, tierReference));
        } else {
            // Add tier
            setLocalMultiSelectedTiers([...localMultiSelectedTiers, tierReference]);
        }
    };

    return (
        <div className="py-2 gap-y-4 text-sm" ref={dropdownRef}>
            <div className="flex mx-3 mt-2 mb-2 justify-end">
                <label className=" w-full flex items-center gap-4 cursor-pointer">
                    <input
                        type={'checkbox'}
                        className={'checkbox-size accent-purpleFont'}
                        checked={localMultiSelectedTiers?.length === profitPoolTierList?.length}
                        onClick={handleSelectAll}
                    />
                    <span className="font-base">All Tiers</span>
                </label>
                <>
                    <span className="w-fit flex justify-center items-center bg-purpleFont text-white rounded-sm px-1">
                        {localMultiSelectedTiers?.length}
                    </span>
                </>
            </div>
            <div className="flex mx-3 mb-2 flex-col space-y-2">
                {profitPoolTierList?.map((tier: any) => (
                    <label key={tier.tierName} className="flex items-center cursor-pointer gap-4 w-full">
                        <input
                            type="checkbox"
                            className={'checkbox-size accent-purpleFont'}
                            checked={localMultiSelectedTiers.includes(tier.tierReference)}
                            onClick={() => handleTierToggle(tier.tierReference)}
                        />
                        <span className="font-base">{tier.tierName}</span>
                    </label>
                ))}
            </div>
        </div>
    );
};

const TierSingleSelectionPanel = ({
    localSingleSelectedTier,
    setLocalSingleSelectedTier,
    dropdownRef,
    profitPoolTierList,
}: {
    localSingleSelectedTier: string[];
    setLocalSingleSelectedTier: Function;
    dropdownRef: React.RefObject<HTMLDivElement>;
    profitPoolTierList: any[];
}) => {
    const handleTierSingleSelection = (tierReference: string) => {
        setLocalSingleSelectedTier([tierReference]);
    };
    return (
        <div className="py-2 gap-y-4 text-sm" ref={dropdownRef}>
            <div className="flex mx-3 mb-2 flex-col space-y-2">
                {profitPoolTierList?.map((tier: any) => (
                    <label key={tier.tierName} className="flex items-center cursor-pointer gap-4 w-full">
                        <input
                            type="radio"
                            className={'checkbox-size accent-purpleFont'}
                            checked={localSingleSelectedTier.includes(tier.tierReference)}
                            onClick={() => handleTierSingleSelection(tier.tierReference)}
                        />
                        <span className="font-base">{tier.tierName}</span>
                    </label>
                ))}
            </div>
        </div>
    );
};

const TierFilter = () => {
    const [isTierFilterExpanded, setIsTierFilterExpanded] = useState(false);
    const [showTierSelectionDropdown, setShowTierSelectionDropdown] = useState(false);
    const {
        profitPoolTierList,
        tempMultiSelectedTiers,
        tempSingleSelectedTier,
        setTempMultiSelectedTiers,
        setTempSingleSelectedTier,
        closeTierFilter,
        setCloseTierFilter,
        isTiersSingleSelect,
    } = useContext(FilterContextData);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownBoxRef = useRef<HTMLDivElement>(null);
    const GlobalFilterNav = document.getElementById('parentGlobalFilterContainer');

    // Local state to track checkbox selections
    const [localMultiSelectedTiers, setLocalMultiSelectedTiers] = useState<string[]>(tempMultiSelectedTiers);
    const [localSingleSelectedTier, setLocalSingleSelectedTier] = useState<string[]>(tempSingleSelectedTier);

    useEffect(() => {
        setLocalMultiSelectedTiers(tempMultiSelectedTiers);
    }, [tempMultiSelectedTiers]);

    useEffect(() => {
        setLocalSingleSelectedTier(tempSingleSelectedTier);
    }, [tempSingleSelectedTier]);

    const normalizeSelectedTiers = useCallback(
        (tiers: string[]): string[] => {
            if (!profitPoolTierList) return tiers;

            const allTierReferences = profitPoolTierList.map((tier: any) => tier.tierReference);
            const allTiersSelected = allTierReferences.length > 0 && tiers.length === allTierReferences.length;

            // If all tiers are selected, return all tier references
            // Otherwise return the selected tiers as is
            return allTiersSelected ? [...allTierReferences] : [...tiers];
        },
        [profitPoolTierList],
    );

    // Function to update context with normalized selection
    const updateContextWithLocalSelection = useCallback(() => {
        const normalizedTiers = normalizeSelectedTiers(localMultiSelectedTiers);
        setTempMultiSelectedTiers(normalizedTiers);
        setTempSingleSelectedTier(localSingleSelectedTier);
    }, [
        localMultiSelectedTiers,
        normalizeSelectedTiers,
        setTempMultiSelectedTiers,
        localSingleSelectedTier,
        setTempSingleSelectedTier,
    ]);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                dropdownBoxRef.current &&
                !dropdownBoxRef.current.contains(event.target as Node)
            ) {
                // Update context with normalized local selection when clicking outside
                updateContextWithLocalSelection();

                setShowTierSelectionDropdown(false);
                // Unable body scroll when dropdown is close
                document.body.style.overflow = 'auto';
                if (GlobalFilterNav) {
                    GlobalFilterNav.style.overflow = 'auto';
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [updateContextWithLocalSelection]);

    const SelectionCapsule = styled.span`
        ${tw` bg-bgBackground p-1.5 rounded text-purpleFont flex w-fit items-center gap-2`}
    `;

    return (
        <div
            className={`flex-col items-center justify-between px-3.2 pb-3.2 border-b border-borderLine font-sm ${
                isTierFilterExpanded && !closeTierFilter ? 'bg-lightPurpleBg border-t' : ''
            }`}
        >
            <div
                className={`flex items-center justify-between pt-3 `}
                onClick={() => {
                    setIsTierFilterExpanded(!isTierFilterExpanded);
                    setCloseTierFilter(false);
                }}
            >
                <span>Tier Filter</span>
                <span>{isTierFilterExpanded && !closeTierFilter ? <Minus /> : <Plus />}</span>
            </div>

            {isTierFilterExpanded && !closeTierFilter ? (
                <div className="inline-block w-full relative">
                    <Popover
                        trigger={'click'}
                        showArrow={false}
                        open={showTierSelectionDropdown}
                        overlayInnerStyle={{
                            position: 'relative',
                            width: '251px',
                            top: 8,
                        }}
                        placement="bottomLeft"
                        overlayClassName="popover-padding-remove"
                        getPopupContainer={(trigger) => trigger.parentElement || document.body}
                        content={
                            !isTiersSingleSelect ? (
                                <TierMultiSelectionPanel
                                    localMultiSelectedTiers={localMultiSelectedTiers}
                                    profitPoolTierList={profitPoolTierList}
                                    setLocalMultiSelectedTiers={setLocalMultiSelectedTiers}
                                    dropdownRef={dropdownRef}
                                />
                            ) : (
                                <TierSingleSelectionPanel
                                    localSingleSelectedTier={localSingleSelectedTier}
                                    setLocalSingleSelectedTier={setLocalSingleSelectedTier}
                                    dropdownRef={dropdownRef}
                                    profitPoolTierList={profitPoolTierList}
                                />
                            )
                        }
                    >
                        <div
                            ref={dropdownBoxRef}
                            className="flex items-center w-full justify-between px-3 py-1 border border-purpleFont bg-white rounded hover:cursor-pointer"
                            onClick={() => {
                                const wasOpen = showTierSelectionDropdown;
                                setShowTierSelectionDropdown(!showTierSelectionDropdown);
                                if (wasOpen) {
                                    updateContextWithLocalSelection();
                                }
                            }}
                        >
                            <>
                                <p>{isTiersSingleSelect ? 'Single Selection' : 'Multiple Selection'}</p>
                                <span className="z-10 font-normal text-center w-8 mr-[-10px] h-10 flex justify-center cursor-pointer items-center flex-row">
                                    <DownSvg />
                                </span>
                            </>
                        </div>

                        <div className={`${showTierSelectionDropdown ? '' : 'hidden'}`}></div>
                    </Popover>
                </div>
            ) : (
                <div className="text-xs">
                    {isTiersSingleSelect ? (
                        <SelectionCapsule>
                            <p className="w-24">
                                {
                                    profitPoolTierList.find((tier) => tier.tierReference === tempSingleSelectedTier[0])
                                        ?.tierName
                                }
                            </p>
                        </SelectionCapsule>
                    ) : (
                        <div className="flex flex-col space-y-2">
                            {tempMultiSelectedTiers?.map((_tier) => {
                                return (
                                    <SelectionCapsule key={_tier}>
                                        <p className="text-xs">
                                            {profitPoolTierList.find((tier) => tier.tierReference === _tier)?.tierName}
                                        </p>
                                    </SelectionCapsule>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TierFilter;
